import React from 'react';
import { FormattedMessage } from 'react-intl';
import translate from '../../i18n/translate';
import { formatBlock, formatCountdown } from './hooks/useMockLiveData';

const DAPP_URL = 'https://sepolia.metha.life/';

function whitepaperFor(locale) {
  if (locale && locale.startsWith('es')) return './documents/Metha_Whitepaper_2026_ES.pdf';
  if (locale && locale.startsWith('fr')) return './documents/Metha_Whitepaper_2026_FR.pdf';
  return './documents/Metha_Whitepaper_2026_EN.pdf';
}

/**
 * Ticker — renders the marquee with the latest on-chain events. We duplicate
 * the list so the CSS marquee animation loops seamlessly.
 */
function Ticker({ events }) {
  const list = events && events.length > 0
    ? events
    : ['Listening for recent on-chain events on Sepolia…'];
  const looped = [...list, ...list];
  return (
    <div className="lv2-live-marquee" aria-hidden="true">
      <span>
        {looped.map((html, i) => (
          <React.Fragment key={i}>
            <span dangerouslySetInnerHTML={{ __html: html }} />
            {'  ·  '}
          </React.Fragment>
        ))}
      </span>
    </div>
  );
}

export default function Hero({ live, locale }) {
  const eth = (live.ethRaisedTotal || 0).toFixed(4);

  return (
    <section className="lv2-hero" id="top">
      <div className="lv2-shell">
        <div className="lv2-hero-meta">
          <span className="lv2-chip">
            <span className="led" />{translate('lv2-chip-live')}
          </span>
          <span className="lv2-chip">
            {translate('lv2-chip-block')} {formatBlock(live.blockNumber)}
          </span>
          <span className="lv2-chip">
            <FormattedMessage id="lv2-chip-period" values={{ n: live.periodId }} />
          </span>
        </div>

        <h1>
          {translate('lv2-hero-h1-pre')} <span className="accent">{translate('lv2-hero-h1-accent')}</span>
        </h1>

        <p className="lede">{translate('lv2-hero-lede')}</p>

        <div className="lv2-hero-cta">
          <a
            className="lv2-btn lv2-btn-prim"
            href={whitepaperFor(locale)}
            target="_blank"
            rel="noopener noreferrer"
          >
            {translate('lv2-hero-cta-paper')} <span className="arrow">→</span>
          </a>
          <a
            className="lv2-btn lv2-btn-ghost"
            href={DAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
          >
            {translate('lv2-hero-cta-dapp')} <span className="arrow">↗</span>
          </a>
        </div>

        {/* Live KPI panel — wired to Sepolia via useSepoliaLive. */}
        <div className="lv2-live-panel" id="protocol">
          <div className="lv2-live-cell">
            <span className="lv2-live-label">{translate('lv2-live-eth-label')}</span>
            <span className="lv2-live-value">{eth}<span className="unit">ETH</span></span>
            <span className="lv2-live-foot">{translate('lv2-live-eth-foot')}</span>
          </div>
          <div className="lv2-live-cell">
            <span className="lv2-live-label">{translate('lv2-live-meth-label')}</span>
            <span className="lv2-live-value">
              {live.methMinted.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              <span className="unit">METH</span>
            </span>
            <span className="lv2-live-foot">{translate('lv2-live-meth-foot-period')}</span>
          </div>
          <div className="lv2-live-cell">
            <span className="lv2-live-label">{translate('lv2-live-period-label')}</span>
            <span className="lv2-live-value">{live.periodId}</span>
            <span className="lv2-live-foot">{translate('lv2-live-period-foot')}</span>
          </div>
          <div className="lv2-live-cell">
            <span className="lv2-live-label">{translate('lv2-live-aud-label')}</span>
            <span className="lv2-live-value">{live.auditorsActive}</span>
            <span className="lv2-live-foot">{translate('lv2-live-aud-foot-status')}</span>
          </div>
          <div className="lv2-live-cell">
            <span className="lv2-live-label">{translate('lv2-live-projects-label')}</span>
            <span className="lv2-live-value">{live.projectsCount}</span>
            <span className="lv2-live-foot">{translate('lv2-live-projects-foot')}</span>
          </div>
          <div className="lv2-live-cell">
            <span className="lv2-live-label">{translate('lv2-live-tvl-label')}</span>
            <span className="lv2-live-value">{live.tvl.toFixed(3)}<span className="unit">ETH</span></span>
            <span className="lv2-live-foot">{translate('lv2-live-tvl-foot')}</span>
          </div>
          <div className="lv2-live-cell lv2-live-cell--time">
            <span className="lv2-live-label">{translate('lv2-live-time-label')}</span>
            <span className="lv2-live-value">{formatCountdown(live.secondsToNextPeriod)}</span>
            <span className="lv2-live-foot lv2-live-foot--rec">
              <span className="lv2-rec-dot" aria-hidden="true" />
              {translate('lv2-live-time-foot')}
            </span>
          </div>
        </div>

        <Ticker events={live.tickerEvents} />
      </div>
    </section>
  );
}
